import { useState } from 'react';
import { EmptyState, TableSkeleton } from '../components/States';
import { useTranslation } from 'react-i18next';
import type { Movement } from '@teo/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge, Panel } from '../components/Panel';
import { Modal } from '../components/Overlay';
import { Pager } from '../components/Pager';
import { variantLabel } from '../components/Operation';
import { useLoad, useSubmit } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';
import { formatDate, formatMoney, formatNumber } from '../lib/format';
import type { Language } from '../i18n';

const REVERSIBLE_STATUS = ['POSTED', 'ACTIVE', 'DISPATCHED', 'CONFIRMED'];
const NOT_REVERSIBLE_TYPES = ['REVERSO', 'DIFERENCIA_FALTANTE', 'DIFERENCIA_AVERIADA'];

/** Kardex: todos los movimientos, solo lectura. Gerencia reversa documentos por contramovimiento (R6). */
export function Movements() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { can } = useAuth();
  const { show, showError } = useToast();
  const { busy, run } = useSubmit();
  const seesCost = can('cost.view');
  const [term, setTerm] = useState('');
  const [applied, setApplied] = useState('');
  const [type, setType] = useState('');
  const [location, setLocation] = useState('');
  const [target, setTarget] = useState<Movement | null>(null);
  const [reason, setReason] = useState('');

  const locations = useLoad(() => api.locations(), []);
  const types = useLoad(() => api.movementTypes(), []);
  const canAudit = can('audit.view');
  const check = useLoad(() => (canAudit ? api.kardexCheck() : Promise.resolve(null)), [canAudit]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const movements = useLoad(
    () => api.movementsPage({ q: applied, movement_type: type, location_id: location ? Number(location) : undefined, limit: pageSize, offset: (page - 1) * pageSize }),
    [applied, type, location, page, pageSize],
  );
  const applySearch = () => {
    setApplied(term.trim());
    setPage(1);
  };
  const nameOf = (id: number | null) => (id == null ? '' : locations.data?.find((l) => l.id === id)?.name ?? '');

  const seen = new Set<number>();
  const reverse = () =>
    run(async () => {
      if (!target?.document_id) return;
      const scope = `reverso:${target.document_id}`;
      try {
        const r = await api.reverseDocument(requestKey(scope), target.document_id, reason);
        releaseKey(scope);
        setTarget(null);
        show(t('kardex.reversed', { doc: r.doc_no }));
        void movements.reload();
        void check.reload();
      } catch (e) {
        showError(e);
      }
    });

  return (
    <Panel
      title={t('kardex.title')}
      description={t('kardex.description')}
      badge={check.data ? <Badge tone={check.data.ok ? 'green' : 'red'}><span id="kardexCheck">{t(check.data.ok ? 'kardex.checkOk' : 'kardex.checkBad', { n: check.data.differences })}</span></Badge> : null}
    >
      <div className="actions" style={{ marginBottom: 11 }}>
        <input
          className="input" id="movSearch" style={{ flex: 1, minWidth: 230 }} placeholder={t('kardex.search')} value={term}
          onChange={(e) => setTerm(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }} onBlur={() => { if (term.trim() !== applied) applySearch(); }}
        />
        <select className="select" id="movType" value={type} onChange={(e) => { setType(e.target.value); setPage(1); }}>
          <option value="">{t('kardex.allTypes')}</option>
          {(types.data ?? []).map((m) => (
            <option key={m.code} value={m.code}>
              {t(`movementType.${m.code}`, { defaultValue: m.name })}
            </option>
          ))}
        </select>
        {(locations.data?.length ?? 0) > 1 ? (
          <select className="select" id="movLocation" value={location} onChange={(e) => { setLocation(e.target.value); setPage(1); }}>
            <option value="">{t('inventory.allLocations')}</option>
            {locations.data?.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th>{t('kardex.cols.date')}</th>
              <th>{t('kardex.cols.type')}</th>
              <th>{t('kardex.cols.doc')}</th>
              <th>{t('kardex.cols.location')}</th>
              <th>{t('kardex.cols.ref')}</th>
              <th className="num">{t('kardex.cols.qty')}</th>
              <th className="num">{t('kardex.cols.price')}</th>
              {seesCost ? <th className="num">{t('kardex.cols.cost')}</th> : null}
              <th>{t('kardex.cols.user')}</th>
              <th>{t('kardex.cols.note')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody id="movBody">
              {movements.loading && !movements.data ? <TableSkeleton cols={11} /> : null}
            {(movements.data?.items ?? []).map((m) => {
              const first = m.document_id != null && !seen.has(m.document_id);
              if (m.document_id != null) seen.add(m.document_id);
              const reversible = can('document.reverse') && first && !NOT_REVERSIBLE_TYPES.includes(m.movement_type) && m.doc_type !== 'REVERSAL' && REVERSIBLE_STATUS.includes(m.document_status ?? '');
              return (
                <tr key={m.id} data-doc={m.doc_no ?? ''} data-type={m.movement_type}>
                  <td>{formatDate(m.occurred_at, lang)}</td>
                  <td>
                    <Badge tone={m.movement_type === 'REVERSO' ? 'red' : m.qty < 0 ? 'amber' : m.qty > 0 ? 'green' : undefined}>{t(`movementType.${m.movement_type}`, { defaultValue: m.movement_type })}</Badge>
                  </td>
                  <td>
                    <b>{m.doc_no}</b>
                    {m.document_status === 'REVERSED' || m.document_status === 'CANCELLED' ? <span className="tiny muted"> {t(`docStatus.${m.document_status}`)}</span> : null}
                  </td>
                  <td>{nameOf(m.location_id) || [nameOf(m.origin_id), nameOf(m.destination_id)].filter(Boolean).join(' → ')}</td>
                  <td>
                    <b>{m.ref}</b> {m.product_name}
                    <br />
                    <span className="tiny muted">{variantLabel(m)}</span>
                  </td>
                  <td className="num" data-col="qty">{m.qty > 0 ? `+${formatNumber(m.qty, lang)}` : formatNumber(m.qty, lang)}</td>
                  <td className="num">{m.unit_price ? formatMoney(m.unit_price, lang) : '—'}</td>
                  {seesCost ? <td className="num">{m.unit_landed_cost != null ? formatMoney(m.unit_landed_cost, lang) : '—'}</td> : null}
                  <td className="tiny">{m.user_name}</td>
                  <td className="tiny">{m.note}</td>
                  <td>
                    {reversible ? (
                      <button type="button" className="btn sm red" data-reverse={m.doc_no ?? ''} onClick={() => { setTarget(m); setReason(''); }}>
                        {t('kardex.reverse')}
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
            {movements.data && movements.data.items.length === 0 ? (
              <tr>
                <td colSpan={11} className="empty">
                  <EmptyState icon="movements" title={t('kardex.empty')} hint={t('kardex.emptyHint')} />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <Pager id="movPager" page={page} pageSize={pageSize} total={movements.data?.total ?? 0} onPage={setPage} onPageSize={(n) => { setPageSize(n); setPage(1); }} />

      <Modal open={!!target} title={t('kardex.reverseTitle', { doc: target?.doc_no ?? '' })} help={t('kardex.reverseHelp')} onClose={() => setTarget(null)}>
        <div className="formgrid">
          <div className="field span2">
            <label htmlFor="reverseReason">{t('kardex.reason')}</label>
            <input id="reverseReason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <button type="button" className="btn red span2" id="reverseConfirm" disabled={busy || !reason.trim()} onClick={() => void reverse()}>
            {t('kardex.reverseConfirm')}
          </button>
        </div>
      </Modal>
    </Panel>
  );
}
